// Base variation and combined product key helpers (shared by leadSourceUtils and trialFormUtils).
// Kept dependency-free so it can be imported by sibling utils without circular imports.

/** Canonical Formik/API value for the AEM / autonomous third product option. */
export const AEM_AUTONOMOUS_PRODUCT_FORM_VALUE = 'aem';

// Product values selectable on the checkbox combined picker
// (Formik `selected_products` entries).
export const CHECKBOX_PRODUCT_REMOTE_ACCESS = 'Remote Access';
export const CHECKBOX_PRODUCT_REMOTE_SUPPORT = 'Remote Support';
export const CHECKBOX_PRODUCT_AEM = AEM_AUTONOMOUS_PRODUCT_FORM_VALUE;

/** HTML checkbox defaults / bad DOM values that must not POST as selected products. */
const CHECKBOX_SELECTED_PRODUCT_SENTINELS = new Set(['', 'on', 'true', 'false']);

/** True when `value` is a non-empty product string safe to append as `selected_products[]`. */
export const isValidCheckboxSelectedProductValue = (value) => (
  typeof value === 'string'
  && value.length > 0
  && !CHECKBOX_SELECTED_PRODUCT_SENTINELS.has(value.toLowerCase())
);

/** Drop phantom checkbox values; keep only real product strings. */
export const filterValidSelectedProductValues = (values) => (
  Array.isArray(values) ? values.filter(isValidCheckboxSelectedProductValue) : []
);

/** True for Formik or cookie keys that represent checkbox product selections. */
export const isSelectedProductsFieldKey = (key) => (
  key === 'selected_products'
  || key === 'selected_products[]'
  || (typeof key === 'string' && key.startsWith('selected_products['))
);

/**
 * Checkbox multi-select product picker (`checkbox-choose` set in Contentful, or
 * `checkbox-combined` forced by the `checkboxSelection` A/B test). More than one product can
 * be selected; each selection reveals + validates its quantity dropdown and the combination
 * drives `lead_source` (see resolveCheckboxLeadSource). Distinct from the single-select picker.
 */
export const isCheckboxCombinedVariation = (variation) => (
  typeof variation === 'string' && variation.includes('checkbox')
);

/** POST selected_products[] when variation is checkbox or Formik already has product picks. */
export const shouldPostCheckboxSelectedProducts = (trialVariation, formValues = {}) => (
  isCheckboxCombinedVariation(trialVariation)
  || filterValidSelectedProductValues(formValues.selected_products).length > 0
);

/** Strip cookie/DOM pollution (e.g. selected_products[]: true) before marketing API POST. */
export const sanitizeCheckboxFormValuesForApi = (values = {}) => {
  const sanitized = { ...values };
  delete sanitized['selected_products[]'];
  if (Array.isArray(sanitized.selected_products)) {
    sanitized.selected_products = filterValidSelectedProductValues(sanitized.selected_products);
  }
  return sanitized;
};

/** Formik quantity field shown + validated for each selected checkbox product. */
export const checkboxProductQuantityField = (product) => {
  if (product === CHECKBOX_PRODUCT_REMOTE_ACCESS) return 'How_many_users_need_remote_access';
  if (product === CHECKBOX_PRODUCT_REMOTE_SUPPORT) return 'how_many_technicians_provide_support';
  if (product === CHECKBOX_PRODUCT_AEM) return 'how_many_endpoints_need_to_manage';
  return null;
};

/**
 * Product picker with Remote Access / Remote Support / … (combined-style variations or
 * `/trial/choose` new-site-radio).
 */
export const isCombinedStyleTrialProductPicker = (variation) => (
  typeof variation === 'string'
  && !isCheckboxCombinedVariation(variation)
  && (variation.includes('combined') || variation.includes('new-site-radio'))
);

/**
 * `/trial/aem-remote-support` — two radios only (Remote Support vs Autonomous Endpoint).
 * Lives here so `leadSourceUtils` can use it without creating a circular import with
 * `trialFormUtils`.
 */
export const isAemRemoteSupportDualProductVariation = (variation) => (
  typeof variation === 'string' && variation === 'aem-remote-support'
);

/**
 * Single-select product picker (combined dropdown, radio cards, or aem-remote-support).
 * Excludes the checkbox multi-select picker.
 */
export const isSingleSelectProductPickerVariation = (variation) => (
  typeof variation === 'string'
  && !isCheckboxCombinedVariation(variation)
  && (
    isCombinedStyleTrialProductPicker(variation)
    || variation.includes('radio')
    || isAemRemoteSupportDualProductVariation(variation)
  )
);

/**
 * Highest-precedence selected product (aem > remote-support > remote-access). Used as the
 * representative `I_want_to_use_Splashtop_for` value submitted to the marketing API.
 */
export const getCheckboxPrimaryProduct = (selectedProducts = []) => {
  const has = (p) => selectedProducts.includes(p);
  if (has(CHECKBOX_PRODUCT_AEM)) return CHECKBOX_PRODUCT_AEM;
  if (has(CHECKBOX_PRODUCT_REMOTE_SUPPORT)) return CHECKBOX_PRODUCT_REMOTE_SUPPORT;
  if (has(CHECKBOX_PRODUCT_REMOTE_ACCESS)) return CHECKBOX_PRODUCT_REMOTE_ACCESS;
  return '';
};

/**
 * `combined_I_want_to_use_Splashtop_for.options` uses Contentful keys; map the autonomous row to
 * {@link AEM_AUTONOMOUS_PRODUCT_FORM_VALUE} so radios/submit send `aem`.
 */
export const remapCombinedAutonomousOptionToFormValue = (options, contentfulAutonomousKey) => {
  if (!options || typeof options !== 'object' || !contentfulAutonomousKey) return options;
  if (!Object.prototype.hasOwnProperty.call(options, contentfulAutonomousKey)) return options;
  const out = { ...options };
  const label = out[contentfulAutonomousKey];
  delete out[contentfulAutonomousKey];
  out[AEM_AUTONOMOUS_PRODUCT_FORM_VALUE] = label;
  return out;
};

/** Selected value is the AEM/autonomous product (canonical or legacy Contentful key). */
export const isAutonomousProductSelection = (iWantToUseValue, contentfulAutonomousKey) => (
  iWantToUseValue === AEM_AUTONOMOUS_PRODUCT_FORM_VALUE
  || (!!contentfulAutonomousKey && iWantToUseValue === contentfulAutonomousKey)
);

/** Base variation segment before the first dash */
export const getBaseVariation = (variation) => {
  const dashIndex = variation.indexOf('-');
  return dashIndex !== -1 ? variation.substring(0, dashIndex) : variation;
};

/**
 * Third combined product value from Contentful combined_I_want_to_use_Splashtop_for.options
 * (optional autonomousEndpointProductKey), or the sole extra option key vs Remote Access / Support.
 */
export const getAutonomousEndpointProductValue = (inputs) => {
  const combined = inputs?.combined_I_want_to_use_Splashtop_for;
  if (!combined?.options || typeof combined.options !== 'object') return null;

  const explicit = combined.autonomousEndpointProductKey;
  if (typeof explicit === 'string' && explicit.trim()) {
    const k = explicit.trim();
    if (Object.prototype.hasOwnProperty.call(combined.options, k)) return k;
  }

  const reserved = new Set(['label', 'Remote Access', 'Remote Support']);
  const extra = Object.keys(combined.options).filter((key) => !reserved.has(key));
  if (extra.length === 1) return extra[0];
  return null;
};

/** Contentful variation slug for AEM retrial (sign-in) forms. */
export const RETRIAL_AEM_VARIATION = 'retrial-aem';

export const isRetrialAemVariation = (variation) => (
  typeof variation === 'string' && variation === RETRIAL_AEM_VARIATION
);

/**
 * AEM retrial variation gate (`aem-retrial`, `retrial-aem`).
 *
 * Historically used to gate both `subhead_retrial` and `submit-retrial`. The
 * subhead key is now applied to every retrial variation via
 * `isRetrialVariation` (`sos-retrial`, `sba-retrial`, `retrial-access`,
 * `retrial-support`, …), matching the pre-#880 behavior; this helper remains
 * the gate for the AEM-only `submit-retrial` button label.
 */
export const usesAemRetrialSubhead = (variation) => (
  isRetrialAemVariation(variation) || variation === 'aem-retrial'
);

/** Same variation gate as {@link usesAemRetrialSubhead} — AEM retrial submit label. */
export const usesAemRetrialSubmitLabel = usesAemRetrialSubhead;

/**
 * Standalone AEM trial field set: endpoints qty dropdown (same as `/trial/aem`), without
 * combined product picker or `aem-remote-support` dual radios. Covers `aem`, `aem-retrial`,
 * and `retrial-aem` (where {@link getBaseVariation} is `retrial`).
 */
export const usesStandaloneAemTrialFields = (variation) => (
  typeof variation === 'string'
  && (getBaseVariation(variation) === 'aem' || isRetrialAemVariation(variation))
  && !isCombinedStyleTrialProductPicker(variation)
  && !isAemRemoteSupportDualProductVariation(variation)
);
